/**
 * Channel async thunks. Use channelAPI for HTTP; dispatch existing action types.
 */
import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  getChannelConfigs,
  saveChannelConfiguration,
  checkIsPrimaryChannel,
  hasChannelIdentifiersForApi,
} from "../../api/channelAPI";
import UserAuth from "../../../auth/userAuth";
import { logger } from "../../../utils/logger";
import {
  ON_CHANNEL_OBJECT_CHANGE,
  ON_CHANNEL_CONFIG_LOAD,
  ON_CHANNEL_DESCRIPTION_UPDATE,
  ON_TOKEN_FAIL,
  ON_SERVER_ERROR_MESSAGE_INVOKE,
  ON_EXCEPTION_MESSAGE_INVOKE,
  ON_IFS_USER_LOAD,
} from "../../constants/actionTypes";
import { requestRefreshAllDocuments } from "../../slices/documents/documentSearchSlice";
import { ON_IS_PRIMARY_CHANNEL } from "../../constants/actionTypes";
import {
  PROGRESS_STATUS,
  USER_TOKEN_STATUS,
  TIME_OUT_STATUS_CODE,
  NAVIGATION_PAGE,
} from "../../constants/metaData";
import { getValidValue } from "../../../functions/helperMethod";

const userAuth = new UserAuth();

/**
 * Wait until a just-dispatched All Documents refresh search settles (loading flips back to false).
 * Only meaningful on the All Documents tab; if the refresh never starts a search (panel inactive or
 * no tenant), give up quickly instead of blocking the save dialog.
 */
const waitForDocumentSearchToSettle = async (getState) => {
  let sawLoading = getState().DocumentSearch?.loading === true;
  const startedAt = Date.now();
  for (;;) {
    await new Promise((resolve) => setTimeout(resolve, 100));
    const loading = getState().DocumentSearch?.loading === true;
    if (loading) sawLoading = true;
    const settled = sawLoading && !loading;
    const neverStarted = !sawLoading && Date.now() - startedAt >= 1500;
    if (settled || neverStarted || Date.now() - startedAt >= 10000) {
      return;
    }
  }
};

/** Load channel config. Same behavior as legacy onChannelConfigLoad. */
export const fetchChannelConfig = createAsyncThunk(
  "channel/fetchChannelConfig",
  async (teamsContext, { dispatch, rejectWithValue }) => {
    logger.info("fetchChannelConfig start", null, "REDUX");
    if (!hasChannelIdentifiersForApi(teamsContext)) {
      dispatch({
        type: ON_CHANNEL_OBJECT_CHANGE,
        payload: {
          channelObjects: [],
          environmentName: "",
          userEmail: "",
          channelDescriptionMissing: false,
        },
      });
      dispatch({ type: ON_CHANNEL_CONFIG_LOAD, payload: PROGRESS_STATUS.noData });
      logger.info("fetchChannelConfig skipped (no Teams channel scope)", null, "REDUX");
      return null;
    }

    const tokenResponse = await userAuth.getUserToken(true);
    const accessToken = tokenResponse?.accessToken ?? null;
    if (!accessToken) {
      logger.error("fetchChannelConfig no access token", tokenResponse, "REDUX");
      dispatch({ type: ON_TOKEN_FAIL, payload: tokenResponse });
      return rejectWithValue("No access token");
    }

    dispatch({ type: ON_CHANNEL_OBJECT_CHANGE, payload: [] });
    dispatch({ type: ON_CHANNEL_CONFIG_LOAD, payload: PROGRESS_STATUS.onLoading });

    try {
      const { data, status } = await getChannelConfigs(teamsContext, accessToken);

      if (status === 200) {
        const channelObjects = data?.channelObjects;
        const hasChannelObjects =
          Array.isArray(channelObjects) && channelObjects.length > 0;

        dispatch({
          type: ON_CHANNEL_OBJECT_CHANGE,
          payload: {
            channelObjects: Array.isArray(channelObjects) ? channelObjects : [],
            environmentName: data?.environmentName ?? "",
            userEmail: data?.userEmail ?? "",
            channelDescriptionMissing: data?.channelDescriptionMissing === true,
          },
        });

        if (hasChannelObjects) {
          dispatch({ type: ON_CHANNEL_CONFIG_LOAD, payload: PROGRESS_STATUS.onCompletion });
          logger.info("fetchChannelConfig success", { status }, "REDUX");
          return data;
        }
        dispatch({ type: ON_CHANNEL_CONFIG_LOAD, payload: PROGRESS_STATUS.noData });
        logger.info("fetchChannelConfig no data", { status }, "REDUX");
        return null;
      }

      if (status === 204) {
        dispatch({
          type: ON_CHANNEL_OBJECT_CHANGE,
          payload: {
            channelObjects: [],
            environmentName: "",
            userEmail: "",
            channelDescriptionMissing: true,
          },
        });
        dispatch({ type: ON_CHANNEL_CONFIG_LOAD, payload: PROGRESS_STATUS.noData });
        return null;
      }

      logger.error("fetchChannelConfig non-200/204", { status }, "REDUX");
      dispatch({
        type: ON_SERVER_ERROR_MESSAGE_INVOKE,
        payload: { response: { status }, statusCode: status, error: null, showMessageBox: false },
      });
      dispatch({ type: ON_CHANNEL_CONFIG_LOAD, payload: PROGRESS_STATUS.onError });
      return rejectWithValue(new Error(`Status ${status}`));
    } catch (err) {
      logger.error("fetchChannelConfig failed", err, "REDUX");
      if (userAuth.isIfsUserLoginRequired?.(err?.response)) {
        dispatch({ type: ON_IFS_USER_LOAD, payload: USER_TOKEN_STATUS.refresh });
      }
      const status = err?.response?.status;
      let errorStatus = PROGRESS_STATUS.onError;
      if (status === 401) errorStatus = PROGRESS_STATUS.onUnauthorized;
      else if (status === 403) errorStatus = PROGRESS_STATUS.onForbidden;
      else if (status === 404) errorStatus = PROGRESS_STATUS.noData;

      dispatch({ type: ON_CHANNEL_CONFIG_LOAD, payload: errorStatus });
      dispatch({
        type: ON_SERVER_ERROR_MESSAGE_INVOKE,
        payload: {
          response: err?.response,
          statusCode: status ?? TIME_OUT_STATUS_CODE,
          error: err,
          showMessageBox: false,
        },
      });
      dispatch({
        type: ON_EXCEPTION_MESSAGE_INVOKE,
        payload: { message: err?.message ?? "", showMessageBox: false },
      });
      return rejectWithValue(err);
    }
  }
);

/** Save channel configuration (object connections) to JSON file. */
export const saveChannelConfigurationThunk = createAsyncThunk(
  "channel/saveChannelConfiguration",
  async ({ teamsContext, objectConnections, activePage }, { dispatch, getState, rejectWithValue }) => {
    logger.info("saveChannelConfigurationThunk start", null, "REDUX");
    const tokenResponse = await userAuth.getUserToken(true);
    const accessToken = tokenResponse?.accessToken ?? null;
    if (!accessToken) {
      logger.error("saveChannelConfigurationThunk no access token", tokenResponse, "REDUX");
      dispatch({ type: ON_TOKEN_FAIL, payload: tokenResponse });
      return rejectWithValue("No access token");
    }

    dispatch({ type: ON_CHANNEL_DESCRIPTION_UPDATE, payload: PROGRESS_STATUS.onLoading });

    try {
      const { status } = await saveChannelConfiguration(teamsContext, accessToken, objectConnections);

      if (status === 200 || status === 204) {
        logger.info("saveChannelConfigurationThunk success", { status }, "REDUX");
        
        // Keep the updating progress visible: refresh the channel config first so get-search
        // runs with the updated object connections, then refresh the document list and hold
        // the progress until that reload finishes when we are on the All Documents tab.
        try {
          await dispatch(fetchChannelConfig(teamsContext));
        } catch (err) {
          logger.warn("saveChannelConfigurationThunk refresh channel config failed", err, "REDUX");
        }

        dispatch(requestRefreshAllDocuments());

        if (activePage === NAVIGATION_PAGE.allDocuments) {
          await waitForDocumentSearchToSettle(getState);
        }

        dispatch({ type: ON_CHANNEL_DESCRIPTION_UPDATE, payload: PROGRESS_STATUS.onCompletion });
        return true;
      }

      logger.error("saveChannelConfigurationThunk non-200/204", { status }, "REDUX");
      dispatch({
        type: ON_SERVER_ERROR_MESSAGE_INVOKE,
        payload: {
          response: { status, data: null },
          statusCode: status,
          error: null,
          showMessageBox: true,
        },
      });
      dispatch({ type: ON_CHANNEL_DESCRIPTION_UPDATE, payload: PROGRESS_STATUS.onError });
      return rejectWithValue(new Error(`Status ${status}`));
    } catch (err) {
      logger.error("saveChannelConfigurationThunk failed", err, "REDUX");
      if (userAuth.isIfsUserLoginRequired?.(err?.response)) {
        dispatch({ type: ON_IFS_USER_LOAD, payload: USER_TOKEN_STATUS.refresh });
      }
      const status = err?.response?.status;
      let errorStatus = PROGRESS_STATUS.onError;
      if (status === 401) errorStatus = PROGRESS_STATUS.onUnauthorized;
      else if (status === 403) errorStatus = PROGRESS_STATUS.onForbidden;

      dispatch({ type: ON_CHANNEL_DESCRIPTION_UPDATE, payload: errorStatus });
      dispatch({
        type: ON_SERVER_ERROR_MESSAGE_INVOKE,
        payload: {
          response: err?.response ? { status: err.response.status, data: err.response.data } : null,
          statusCode: status ?? TIME_OUT_STATUS_CODE,
          error: { message: err?.message },
          showMessageBox: true,
        },
      });
      return rejectWithValue(err);
    }
  }
);

/** Check if the current channel is the team's primary (General) channel. */
export const fetchIsPrimaryChannel = createAsyncThunk(
  "channel/fetchIsPrimaryChannel",
  async (teamsContext, { dispatch, rejectWithValue }) => {
    logger.info("fetchIsPrimaryChannel start", null, "REDUX");
    if (!hasChannelIdentifiersForApi(teamsContext)) {
      dispatch({ type: ON_IS_PRIMARY_CHANNEL, payload: { isPrimaryChannel: false } });
      logger.info("fetchIsPrimaryChannel skipped (no Teams channel scope)", null, "REDUX");
      return null;
    }

    const tokenResponse = await userAuth.getUserToken(true);
    const accessToken = tokenResponse?.accessToken ?? null;
    if (!accessToken) {
      logger.error("fetchIsPrimaryChannel no access token", tokenResponse, "REDUX");
      dispatch({ type: ON_TOKEN_FAIL, payload: tokenResponse });
      return rejectWithValue("No access token");
    }

    try {
      const { data, status } = await checkIsPrimaryChannel(teamsContext, accessToken);

      if (status === 200) {
        const isPrimary = data?.isPrimaryChannel === true;
        dispatch({ type: ON_IS_PRIMARY_CHANNEL, payload: { isPrimaryChannel: isPrimary } });
        logger.info("fetchIsPrimaryChannel success", { isPrimary }, "REDUX");
        return { isPrimary };
      }

      logger.error("fetchIsPrimaryChannel non-200", { status }, "REDUX");
      dispatch({ type: ON_IS_PRIMARY_CHANNEL, payload: { isPrimaryChannel: false } });
      return rejectWithValue(new Error(`Status ${status}`));
    } catch (err) {
      logger.error("fetchIsPrimaryChannel failed", err, "REDUX");
      dispatch({ type: ON_IS_PRIMARY_CHANNEL, payload: { isPrimaryChannel: false } });
      return rejectWithValue(err);
    }
  }
);
